/**
 * Live User Prompt Tester
 * 
 * Comprehensive end-to-end testing interface that simulates real user interactions
 * with the AI agent system. Tests the complete workflow:
 * 1. User inputs natural language prompt
 * 2. Orchestrator analyzes intent
 * 3. GPT-5 Nano Agent creates structured action plan
 * 4. Graph API agents execute real API calls
 * 5. Results are synthesized and displayed
 * 
 * This page allows testing the entire system with actual user prompts to verify
 * that the AI correctly interprets instructions and makes the right API calls.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { createClient } from '@blinkdotnew/sdk';
import { processWithOrchestrator, OrchestrationResult, OrchestrationStep } from '@/services/orchestratorMiddleware';
import { 
  Send, 
  Search, 
  Calendar, 
  Mail, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Loader2,
  Play,
  AlertCircle,
  Info,
  Zap
} from 'lucide-react';
import toast from 'react-hot-toast';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

/**
 * Predefined test scenarios covering common user requests
 */
const TEST_SCENARIOS = {
  email: [
    {
      name: 'Send Simple Email',
      prompt: 'Send an email to np@naible.ai with subject "Test Email" and body "This is a test from the AI agent system"',
      expectedAction: 'email_send',
      expectedAPIs: ['POST /me/sendMail']
    },
    {
      name: 'Send Email with CC',
      prompt: 'Send email to john@example.com, CC sarah@example.com, subject "Weekly Update", body "Here is the weekly update"',
      expectedAction: 'email_send',
      expectedAPIs: ['POST /me/sendMail']
    },
    {
      name: 'Search Emails',
      prompt: 'Find all emails from np@naible.ai from last week',
      expectedAction: 'email_search',
      expectedAPIs: ['GET /me/messages', 'POST /outlook-search']
    },
    {
      name: 'Create Draft',
      prompt: 'Create a draft email to team@company.com about the quarterly review',
      expectedAction: 'email_draft',
      expectedAPIs: ['POST /me/messages']
    },
    {
      name: 'Reply to Email',
      prompt: 'Reply to the last email from np@naible.ai saying "Thanks for the update!"',
      expectedAction: 'email_reply',
      expectedAPIs: ['POST /me/messages/{id}/reply']
    }
  ],
  calendar: [
    {
      name: 'Create Event',
      prompt: 'Schedule a meeting tomorrow at 2pm for 1 hour about project planning',
      expectedAction: 'calendar_create',
      expectedAPIs: ['POST /me/calendar/events']
    },
    {
      name: 'Find Events',
      prompt: 'Show me all my meetings this week',
      expectedAction: 'calendar_search',
      expectedAPIs: ['GET /me/calendar/calendarView']
    },
    {
      name: 'Update Event',
      prompt: 'Reschedule my 2pm meeting to 3pm',
      expectedAction: 'calendar_update',
      expectedAPIs: ['PATCH /me/events/{id}']
    },
    {
      name: 'Create Event with Attendees',
      prompt: 'Create a team sync on Friday at 10am with john@example.com and sarah@example.com',
      expectedAction: 'calendar_create',
      expectedAPIs: ['POST /me/calendar/events']
    }
  ],
  complex: [
    {
      name: 'Multi-step Email Workflow',
      prompt: 'Find all unread emails from last week and mark them as read',
      expectedAction: 'email_search',
      expectedAPIs: ['GET /me/messages', 'PATCH /me/messages/{id}']
    },
    {
      name: 'Calendar + Email',
      prompt: 'Find my next meeting and send the agenda to all attendees',
      expectedAction: 'calendar_search',
      expectedAPIs: ['GET /me/calendar/events', 'POST /me/sendMail']
    }
  ]
};

interface TestResult {
  scenarioName: string;
  prompt: string;
  expectedAction: string;
  actualAction: string | null;
  expectedAPIs: string[];
  actualAPIs: string[];
  orchestrationResult: OrchestrationResult | null;
  success: boolean;
  duration: number;
  error?: string;
  timestamp: Date;
}

export default function LiveUserPromptTester() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  
  // Real-time orchestration tracking
  const [currentSteps, setCurrentSteps] = useState<OrchestrationStep[]>([]);
  const [currentResult, setCurrentResult] = useState<OrchestrationResult | null>(null);
  
  // Test results history
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<'email' | 'calendar' | 'complex'>('email');

  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      setUser(state.user);
    });
    return unsubscribe;
  }, []);

  /**
   * Execute a test scenario
   */
  const executeTest = async (scenario: typeof TEST_SCENARIOS.email[0]) => {
    if (!user) {
      toast.error('Please sign in first');
      return;
    }

    setLoading(true);
    setCurrentSteps([]);
    setCurrentResult(null);
    
    const startTime = Date.now();

    try {
      console.log('🧪 Executing test:', scenario.name);
      console.log('📝 Prompt:', scenario.prompt);

      // Process through orchestrator with real-time step tracking
      const result = await processWithOrchestrator(
        scenario.prompt,
        user.id,
        (step: OrchestrationStep) => {
          setCurrentSteps(prev => {
            const existing = prev.findIndex(s => s.id === step.id);
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = step;
              return updated;
            }
            return [...prev, step];
          });
        }
      );

      setCurrentResult(result);
      
      const duration = Date.now() - startTime;

      // Extract actual APIs called
      const actualAPIs = extractAPICalls(result);
      
      // Determine actual action taken
      const actualAction = result.agentResponse?.action.type || null;

      // Create test result
      const testResult: TestResult = {
        scenarioName: scenario.name,
        prompt: scenario.prompt,
        expectedAction: scenario.expectedAction,
        actualAction,
        expectedAPIs: scenario.expectedAPIs,
        actualAPIs,
        orchestrationResult: result,
        success: result.success && actualAction === scenario.expectedAction,
        duration,
        timestamp: new Date()
      };

      setTestResults(prev => [testResult, ...prev]);

      if (testResult.success) {
        toast.success(`✅ Test passed: ${scenario.name}`);
      } else {
        toast.error(`❌ Test failed: ${scenario.name}`);
      }

    } catch (error) {
      const duration = Date.now() - startTime;
      const testResult: TestResult = {
        scenarioName: scenario.name,
        prompt: scenario.prompt,
        expectedAction: scenario.expectedAction,
        actualAction: null,
        expectedAPIs: scenario.expectedAPIs,
        actualAPIs: [],
        orchestrationResult: null,
        success: false,
        duration,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date()
      };

      setTestResults(prev => [testResult, ...prev]);
      toast.error(`❌ Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Execute custom user prompt
   */
  const executeCustomPrompt = async () => {
    if (!user) {
      toast.error('Please sign in first');
      return;
    }

    if (!customPrompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setLoading(true);
    setCurrentSteps([]);
    setCurrentResult(null);
    
    const startTime = Date.now();

    try {
      console.log('🧪 Executing custom prompt:', customPrompt);

      const result = await processWithOrchestrator(
        customPrompt,
        user.id,
        (step: OrchestrationStep) => {
          setCurrentSteps(prev => {
            const existing = prev.findIndex(s => s.id === step.id);
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = step;
              return updated;
            }
            return [...prev, step];
          });
        }
      );

      setCurrentResult(result);
      
      const duration = Date.now() - startTime;
      const actualAPIs = extractAPICalls(result);
      const actualAction = result.agentResponse?.action.type || null;

      const testResult: TestResult = {
        scenarioName: 'Custom Prompt',
        prompt: customPrompt,
        expectedAction: 'unknown',
        actualAction,
        expectedAPIs: [],
        actualAPIs,
        orchestrationResult: result,
        success: result.success,
        duration,
        timestamp: new Date()
      };

      setTestResults(prev => [testResult, ...prev]);

      if (result.success) {
        toast.success('✅ Prompt executed successfully');
      } else {
        toast.error('❌ Prompt execution failed');
      }

    } catch (error) {
      toast.error(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Extract API calls from orchestration result
   */
  const extractAPICalls = (result: OrchestrationResult): string[] => {
    const apis: string[] = [];
    
    if (result.agentResponse?.action.type) {
      // Map action types to API endpoints
      const actionToAPI: Record<string, string[]> = {
        'email_send': ['POST /me/sendMail'],
        'email_search': ['GET /me/messages'],
        'email_draft': ['POST /me/messages'],
        'email_reply': ['POST /me/messages/{id}/reply'],
        'email_move': ['POST /me/messages/{id}/move'],
        'calendar_create': ['POST /me/calendar/events'],
        'calendar_search': ['GET /me/calendar/calendarView'],
        'calendar_update': ['PATCH /me/events/{id}']
      };

      const apiCalls = actionToAPI[result.agentResponse.action.type];
      if (apiCalls) {
        apis.push(...apiCalls);
      }
    }

    return apis;
  };

  /**
   * Get status icon for orchestration step
   */
  const getStepIcon = (status: OrchestrationStep['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'running':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  /**
   * Get agent icon
   */
  const getAgentIcon = (agent: OrchestrationStep['agent']) => {
    switch (agent) {
      case 'email-agent':
        return <Mail className="w-4 h-4" />;
      case 'calendar-agent':
        return <Calendar className="w-4 h-4" />;
      case 'search-agent':
        return <Search className="w-4 h-4" />;
      default:
        return <Zap className="w-4 h-4" />;
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please sign in to run live tests</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => blink.auth.login()} className="w-full">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Live User Prompt Tester</h1>
          <p className="text-muted-foreground mt-2">
            Test the AI agent system with real user prompts and verify Graph API integration
          </p>
        </div>

        {/* Custom Prompt Testing */}
        <Card>
          <CardHeader>
            <CardTitle>Test Custom Prompt</CardTitle>
            <CardDescription>
              Enter any natural language command to test how the AI interprets and executes it
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="E.g., 'Send an email to john@example.com with subject Meeting Reminder'"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !loading && executeCustomPrompt()}
                disabled={loading}
              />
              <Button 
                onClick={executeCustomPrompt} 
                disabled={loading || !customPrompt.trim()}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                <span className="ml-2">Test</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Predefined Test Scenarios */}
        <Card>
          <CardHeader>
            <CardTitle>Predefined Test Scenarios</CardTitle>
            <CardDescription>
              Run common test cases to verify end-to-end functionality
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={selectedCategory} onValueChange={(v) => setSelectedCategory(v as any)}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="email">
                  <Mail className="w-4 h-4 mr-2" />
                  Email Tests
                </TabsTrigger>
                <TabsTrigger value="calendar">
                  <Calendar className="w-4 h-4 mr-2" />
                  Calendar Tests
                </TabsTrigger>
                <TabsTrigger value="complex">
                  <Zap className="w-4 h-4 mr-2" />
                  Complex Tests
                </TabsTrigger>
              </TabsList>

              {Object.entries(TEST_SCENARIOS).map(([category, scenarios]) => (
                <TabsContent key={category} value={category} className="space-y-2 mt-4">
                  {scenarios.map((scenario, index) => (
                    <Card key={index} className="border-l-4 border-l-primary/20">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold mb-1">{scenario.name}</h4>
                            <p className="text-sm text-muted-foreground mb-2">{scenario.prompt}</p>
                            <div className="flex gap-2 flex-wrap">
                              <Badge variant="outline">{scenario.expectedAction}</Badge>
                              {scenario.expectedAPIs.map((api, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {api}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => executeTest(scenario)}
                            disabled={loading}
                          >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

        {/* Real-time Orchestration Workflow */}
        {currentSteps.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Orchestration Workflow</CardTitle>
              <CardDescription>
                Live view of the AI agent workflow execution
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {currentSteps.map((step, index) => (
                  <div key={step.id} className="flex items-start gap-3 p-3 border rounded-lg">
                    <div className="flex-shrink-0 mt-1">
                      {getStepIcon(step.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {getAgentIcon(step.agent)}
                        <span className="font-semibold text-sm">
                          Step {index + 1}: {step.type.toUpperCase()}
                        </span>
                        <Badge variant="outline" className="ml-auto">
                          {step.agent}
                        </Badge>
                        {step.endTime && step.startTime && (
                          <Badge variant="secondary">
                            {step.endTime - step.startTime}ms
                          </Badge>
                        )}
                      </div>
                      {step.error && (
                        <Alert variant="destructive" className="mt-2">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{step.error}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Result Details */}
        {currentResult && (
          <Card>
            <CardHeader>
              <CardTitle>Execution Result</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold">Status:</label>
                  <div className="flex items-center gap-2 mt-1">
                    {currentResult.success ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <span className={currentResult.success ? 'text-green-600' : 'text-red-600'}>
                      {currentResult.success ? 'Success' : 'Failed'}
                    </span>
                    <Badge variant="secondary" className="ml-auto">
                      {currentResult.totalDuration}ms
                    </Badge>
                  </div>
                </div>

                <Separator />

                <div>
                  <label className="text-sm font-semibold">User Intent:</label>
                  <p className="text-sm text-muted-foreground mt-1">{currentResult.userIntent}</p>
                </div>

                {currentResult.agentResponse && (
                  <>
                    <Separator />
                    <div>
                      <label className="text-sm font-semibold">Agent Action:</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge>{currentResult.agentResponse.action.type}</Badge>
                        <Badge variant="outline">
                          {currentResult.agentResponse.action.confidence}% confidence
                        </Badge>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-semibold">Parameters:</label>
                      <pre className="bg-muted p-3 rounded-lg text-xs mt-1 overflow-auto">
                        {JSON.stringify(currentResult.agentResponse.action.parameters, null, 2)}
                      </pre>
                    </div>
                  </>
                )}

                <Separator />

                <div>
                  <label className="text-sm font-semibold">AI Explanation:</label>
                  <div className="bg-muted p-3 rounded-lg text-sm mt-1">
                    {currentResult.synthesizedExplanation}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Test Results History */}
        {testResults.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Test Results History</CardTitle>
              <CardDescription>
                {testResults.filter(r => r.success).length} / {testResults.length} tests passed
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {testResults.map((result, index) => (
                    <div 
                      key={index}
                      className={`p-3 border rounded-lg ${
                        result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {result.success ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                          <span className="font-semibold text-sm">{result.scenarioName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {result.duration}ms
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {result.timestamp.toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{result.prompt}</p>
                      <div className="flex gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          Expected: {result.expectedAction}
                        </Badge>
                        <Badge variant={result.actualAction === result.expectedAction ? 'default' : 'destructive'} className="text-xs">
                          Actual: {result.actualAction || 'none'}
                        </Badge>
                      </div>
                      {result.error && (
                        <Alert variant="destructive" className="mt-2">
                          <AlertDescription className="text-xs">{result.error}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
